import { useRef, useState, type FormEvent } from 'react';
import { Check, FileText, Loader2 } from 'lucide-react';
import type { TurnstileInstance } from '@marsidev/react-turnstile';
import type { FamilyManual } from '@evi/store-kit';
import { ShopTurnstile, hasTurnstile } from './ShopTurnstile';
import { submitLead } from '@/lib/api';
import styles from './ManualDownload.module.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Shop product-manual download, mirroring the in-app ManualCaptureSheet pattern
 * for the public shop: click "Download the manual", leave an email, then the PDF
 * downloads. The lead posts to /public/leads (unauthenticated) with the family's
 * spec-sheet source. Turnstile is FAIL-CLOSED on the shop, so the token is
 * required when a site key is configured; a Turnstile rejection blocks the
 * download, but ANY other lead failure still downloads (the manual is the user's,
 * the lead is ours to lose). Keyless builds skip the lead and just download.
 */
async function errorCode(err: unknown): Promise<string | null> {
  const resp = (err as { response?: Response }).response;
  if (!resp) return null;
  try {
    const body = (await resp.clone().json()) as { error?: { code?: string } };
    return body?.error?.code ?? null;
  } catch {
    return null;
  }
}

export function ManualDownload({ manual }: { manual: FamilyManual }) {
  const [phase, setPhase] = useState<'idle' | 'form' | 'done'>('idle');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tsRef = useRef<TurnstileInstance>(null);

  const startDownload = (): void => {
    const a = document.createElement('a');
    a.href = manual.path;
    a.download = '';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const canSubmit =
    EMAIL_RE.test(email.trim()) && !busy && (!hasTurnstile || !!token);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSubmit) return;
    // Keyless build: no captcha to satisfy the fail-closed server, so skip the
    // lead and just hand over the manual.
    if (!hasTurnstile || !token) {
      startDownload();
      setPhase('done');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await submitLead({ email: email.trim(), source: manual.leadSource, turnstile_token: token });
    } catch (err) {
      const code = await errorCode(err);
      if (code === 'TURNSTILE_FAILED' || code === 'TURNSTILE_REQUIRED') {
        // Spent/blocked token: reset for a fresh challenge, hold the download.
        tsRef.current?.reset();
        setToken(null);
        setError('Captcha verification failed. Please try again.');
        setBusy(false);
        return;
      }
      // Non-Turnstile failure: the lead is lost, the download is not.
    }
    startDownload();
    setPhase('done');
    setBusy(false);
  };

  if (phase === 'done') {
    return (
      <p className={styles.done}>
        <Check size={15} /> Your download has started. Setup tips are on the way.
      </p>
    );
  }

  if (phase === 'idle') {
    return (
      <button type="button" className={`eviBtn eviBtnGhost ${styles.trigger}`} onClick={() => setPhase('form')}>
        <FileText size={15} /> Download the manual
      </button>
    );
  }

  return (
    <form className={styles.form} onSubmit={onSubmit} noValidate>
      <p className={styles.blurb}>We&rsquo;ll send product updates and setup tips to this address.</p>
      <input
        type="email"
        className={`${styles.input} clarity-mask`}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        autoComplete="email"
        required
        disabled={busy}
      />
      <ShopTurnstile ref={tsRef} onToken={setToken} />
      {!hasTurnstile && <p className={styles.err}>Verification is unavailable; the manual will still download.</p>}
      {error && <p className={styles.err}>{error}</p>}
      <button type="submit" className="eviBtn eviBtnPrimary" disabled={!canSubmit}>
        {busy ? <Loader2 size={15} className={styles.spin} /> : <><FileText size={15} /> Get the manual</>}
      </button>
    </form>
  );
}
